import { uploadEmployeeAvatarApi } from "@/api/services/global/employee";
import { Avatar } from "@/components/elements/Avatar";
import { DropZone } from "@/components/elements/DropZone";
import { FILE_UPLOAD_FOR, MIME_TYPES } from "@/constants/file";
import { INVALID_PASSWORD_PROTECTED_PDF, INVALID_PDF_FORMAT } from "@/constants/invalids";
import { divider } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { CHECK_PDF_RESULT } from "@/types/enum";
import { TExtendFile } from "@/types/file";
import { showError } from "@/utils/error";
import { checkFileListWhetherIsActualPdf, convertKbToByte, validateRatio } from "@/utils/file";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { Box, Stack, Typography, styled } from "@mui/material";
import React from "react";
import { useParams, useSearchParams } from "react-router-dom";

export type TUploadAvatarDropZoneProps = {
  avatarFile: TExtendFile | undefined;
  setAvatarFile: React.Dispatch<React.SetStateAction<TExtendFile | undefined>>;
};

export const UploadAvatarDropZone: FCC<TUploadAvatarDropZoneProps> = ({ setAvatarFile, avatarFile }) => {
  const { id: employeeId } = useParams();
  const [searchParams] = useSearchParams();

  const onChangeFile = async (value: TExtendFile | TExtendFile[] | null) => {
    const file = [value].flat().at(0);

    if (file?.size === 0) {
      toast.error("0Byteのファイルはアップロードできません。");
      return;
    }

    const result = file && (await checkFileListWhetherIsActualPdf([file]));
    if (result === CHECK_PDF_RESULT.PASSWORD_PROTECTED) {
      toast.error(INVALID_PASSWORD_PROTECTED_PDF);
      return;
    }
    if (result === CHECK_PDF_RESULT.INVALID) {
      toast.error(INVALID_PDF_FORMAT);
      return;
    }

    if (toNumber(file?.size) > convertKbToByte(10000)) {
      toast.error("ファイルサイズは10MB以内でお願いします。");
      return;
    }

    const isValidRatio = await validateRatio(file as File);
    if (!isValidRatio) {
      toast.error("縦横比４：３で縦長になるようにサイズ調整してください。");
      return;
    }
    if (!file) {
      setAvatarFile(undefined);
      return;
    }
    const token = searchParams.get("token");
    const companyId = toNumber(searchParams.get("companyId"));
    if (!employeeId || !token || !companyId) return;
    try {
      const avatarFilePath = await uploadEmployeeAvatarApi(employeeId, {
        companyId,
        employeeId: toNumber(employeeId),
        file,
        token,
        uploadFor: FILE_UPLOAD_FOR.EMPLOYEE_AVATAR_FILE,
      });

      setAvatarFile(Object.assign(file, { filePath: avatarFilePath.data.filePath }));
    } catch (error) {
      showError({ message: "このURLは無効であるか、以前に使用されています。" });
    }
  };

  return (
    <>
      {avatarFile ? (
        <Avatar imageSrc={URL.createObjectURL(avatarFile)} width={300} height={400} onRemove={() => setAvatarFile(undefined)} />
      ) : (
        <Container>
          <DropZone
            options={{ multiple: false }}
            rules={{ acceptMimeTypes: MIME_TYPES.IMAGE, maxSize: convertKbToByte(10000) }}
            enableFileList={false}
            onChange={onChangeFile}
          >
            <Stack width={352} alignItems="center">
              <Avatar width={300} height={400} onRemove={() => setAvatarFile(undefined)} />
              <Typography sx={{ textAlign: "center", mt: "16px", whiteSpace: "pre-line" }} variant="body14">
                {`画像ファイルを\nドラッグ&ドロップ`}
              </Typography>
            </Stack>
          </DropZone>
        </Container>
      )}
    </>
  );
};

const Container = styled(Box)`
  padding: 24px;
  border: 1px solid ${divider.middle};
  border-radius: ${rounded.sm};
`;
